import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { RealEstateState, Property } from '@/types'

const initialState: RealEstateState = {
  properties: [],
  totalValue: 0,
  totalMortgageDebt: 0,
  totalEquity: 0,
  totalRentalIncome: 0,
  chartData: {
    labels: [],
    propertyValues: [],
    equityValues: [],
    debtValues: [],
    interestPaid: [],
    rentalProfit: [],
    rentalIncome: [],
    rentalUpkeep: [],
    rentalInterest: [],
  },
}

const realEstateSlice = createSlice({
  name: 'realEstate',
  initialState,
  reducers: {
    addProperty: (state, action: PayloadAction<Omit<Property, 'id' | 'equity'>>) => {
      const property: Property = {
        id: Date.now(),
        ...action.payload,
        equity: action.payload.value - (action.payload.mortgageBalance || 0),
      }
      state.properties.push(property)
      realEstateSlice.caseReducers.updateTotals(state)
      realEstateSlice.caseReducers.updateChartData(state)
    },
    updateProperty: (state, action: PayloadAction<{ id: number; updates: Partial<Property> }>) => {
      const { id, updates } = action.payload
      const index = state.properties.findIndex((prop) => prop.id === id)
      if (index !== -1) {
        const currentProperty = state.properties[index]
        state.properties[index] = {
          ...currentProperty,
          ...updates,
          equity: (updates.value || currentProperty.value) - (updates.mortgageBalance ?? currentProperty.mortgageBalance ?? 0),
        }
        realEstateSlice.caseReducers.updateTotals(state)
        realEstateSlice.caseReducers.updateChartData(state)
      }
    },
    removeProperty: (state, action: PayloadAction<number>) => {
      state.properties = state.properties.filter((prop) => prop.id !== action.payload)
      realEstateSlice.caseReducers.updateTotals(state)
      realEstateSlice.caseReducers.updateChartData(state)
    },
    setRealEstateData: (state, action: PayloadAction<Partial<RealEstateState>>) => {
      const data = action.payload
      if (data.properties) state.properties = data.properties
      if (data.totalValue !== undefined) state.totalValue = data.totalValue
      if (data.totalMortgageDebt !== undefined) state.totalMortgageDebt = data.totalMortgageDebt
      if (data.totalEquity !== undefined) state.totalEquity = data.totalEquity
      if (data.totalRentalIncome !== undefined) state.totalRentalIncome = data.totalRentalIncome
      if (data.chartData) state.chartData = { ...state.chartData, ...data.chartData }
      realEstateSlice.caseReducers.updateTotals(state)
      realEstateSlice.caseReducers.updateChartData(state)
    },
    setProperties: (state, action: PayloadAction<Property[]>) => {
      state.properties = action.payload
      realEstateSlice.caseReducers.updateTotals(state)
      realEstateSlice.caseReducers.updateChartData(state)
    },
    updateTotals: (state) => {
      state.totalValue = state.properties.reduce((sum, prop) => sum + prop.value, 0)
      state.totalMortgageDebt = state.properties.reduce((sum, prop) => sum + (prop.mortgageBalance || 0), 0)
      state.totalEquity = state.totalValue - state.totalMortgageDebt
      state.totalRentalIncome = state.properties.reduce((sum, prop) => {
        if (prop.type === 'rental' || prop.type === 'commercial') {
          const annualRent = (prop.rent || 0) * 12
          const annualExpenses = (prop.expenses || 0) * 12
          return sum + (annualRent - annualExpenses)
        }
        return sum
      }, 0)
    },
    updateChartData: (state) => {
      const years = 30
      const labels: string[] = []
      const propertyValues: number[] = []
      const equityValues: number[] = []
      const debtValues: number[] = []
      const interestPaid: number[] = []
      const rentalProfit: number[] = []
      const rentalIncome: number[] = []
      const rentalUpkeep: number[] = []
      const rentalInterest: number[] = []

      // Simulation state per property
      const simStates = state.properties.map((p) => ({
        remainingBalance: p.mortgageBalance || 0,
        monthlyRate: (p.mortgageRate || 0) / 100 / 12,
        monthlyPayment: p.monthlyPayment || 0,
        type: p.type,
        rent: p.rent || 0,
        rentGrowth: p.rentGrowth || 0,
        expenses: p.expenses || 0,
        appreciation: p.appreciation || 0,
        value: p.value || 0,
      }))

      let cumulativeInterestAll = 0

      for (let year = 0; year <= years; year++) {
        labels.push(`Year ${year}`)

        let totalValue = 0
        let totalDebt = 0
        let totalInterestYearAll = 0
        let totalAnnualProfit = 0
        let totalAnnualRent = 0
        let totalAnnualUpkeep = 0
        let totalAnnualRentalInterest = 0

        state.properties.forEach((property, idx) => {
          const futureValue = property.value * Math.pow(1 + property.appreciation / 100, year)
          totalValue += futureValue

          if (property.mortgageBalance > 0 && property.mortgageRate > 0 && property.monthlyPayment > 0) {
            const sim = simStates[idx]
            let interestYear = 0
            
            for (let m = 0; m < 12; m++) {
              if (sim.remainingBalance <= 0) break
              const interestMonth = sim.remainingBalance * sim.monthlyRate
              const payment = Math.min(sim.monthlyPayment, sim.remainingBalance + interestMonth)
              const principalPayment = Math.max(0, payment - interestMonth)
              sim.remainingBalance = Math.max(0, sim.remainingBalance - principalPayment)
              interestYear += interestMonth
            }

            totalDebt += sim.remainingBalance
            totalInterestYearAll += interestYear

            if (property.type === 'rental' || property.type === 'commercial') {
              const annualRent = sim.rent * 12 * Math.pow(1 + sim.rentGrowth / 100, year)
              const annualUpkeep = sim.expenses * 12
              const profitYear = annualRent - annualUpkeep - interestYear
              totalAnnualProfit += profitYear
              totalAnnualRent += annualRent
              totalAnnualUpkeep += annualUpkeep
              totalAnnualRentalInterest += interestYear
            }
          } else if (property.mortgageBalance > 0) {
            const totalPayments = (property.monthlyPayment || 0) * 12 * year
            const remainingBalance = Math.max(0, property.mortgageBalance - totalPayments)
            totalDebt += remainingBalance
            
            if (property.type === 'rental' || property.type === 'commercial') {
              const annualRent = (property.rent || 0) * 12 * Math.pow(1 + (property.rentGrowth || 0) / 100, year)
              const annualUpkeep = (property.expenses || 0) * 12
              totalAnnualProfit += annualRent - annualUpkeep
              totalAnnualRent += annualRent
              totalAnnualUpkeep += annualUpkeep
            }
          }
        })

        propertyValues.push(totalValue)
        debtValues.push(totalDebt)
        equityValues.push(totalValue - totalDebt)
        cumulativeInterestAll += totalInterestYearAll
        interestPaid.push(cumulativeInterestAll)
        rentalProfit.push(totalAnnualProfit)
        rentalIncome.push(totalAnnualRent)
        rentalUpkeep.push(totalAnnualUpkeep)
        rentalInterest.push(totalAnnualRentalInterest)
      }

      state.chartData = {
        labels,
        propertyValues,
        equityValues,
        debtValues,
        interestPaid,
        rentalProfit,
        rentalIncome,
        rentalUpkeep,
        rentalInterest,
      }
    },
    recalculateChartData: (state) => {
      realEstateSlice.caseReducers.updateChartData(state)
    },
  },
})

export const {
  addProperty,
  updateProperty,
  removeProperty,
  setRealEstateData,
  setProperties,
  recalculateChartData,
} = realEstateSlice.actions

export default realEstateSlice.reducer
